import { Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useRef, useState } from 'react'

import { useProductForm } from '@/hooks/features/products/useProductForm'
import { AssistantReviewContext, useProductProposal } from '@/components/assistant/useProductProposal'
import { useStockProposal } from '@/components/assistant/useStockProposal'
import { ReviewBanner } from '@/components/assistant/ReviewBanner'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Tabs, TabsContent } from '@/components/ui/tabs'

import { BackButton, ConfirmDialog, PageHeader, StatusBadge, UnderlinedTabsList } from '@/components/atoms'
import { GeneralSection } from '@/components/features/products/form/GeneralSection'
import { SeoSection } from '@/components/features/products/form/SeoSection'
import { VariantsSection } from '@/components/features/products/form/VariantsSection'
import { ProductInventorySection } from '@/components/features/products/form/ProductInventorySection'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

export const ProductDetail = () => {
  const navigate = useNavigate()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { id } = useParams<{ id: string }>()
  // The assistant's proposal (if any) is marked saved when the normal save succeeds.
  const savedRef = useRef<() => void>()
  const {
    form,
    onSubmit,
    isSubmitting,
    isLoading,
    product,
    fetchProduct,
    attributes,
    attributesLoading,
    categories,
    categoriesLoading,
    brands,
    brandsLoading,
    handleDelete,
  } = useProductForm(id, { onSaved: () => savedRef.current?.() })
  const review = useProductProposal(form, { productId: id, ready: Boolean(product) && !isLoading })
  savedRef.current = review.onSaved
  const [tab, setTab] = useState('general')
  const stockReview = useStockProposal(id, () => setTab('inventory'))

  useEnterSubmit(form.handleSubmit(onSubmit), !isSubmitting)

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <Spinner className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading product…</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="mx-auto w-full max-w-[1088px] pt-10">
        <Card className="flex flex-col items-center gap-3 px-4 py-12 text-center">
          <h2 className="text-heading-md text-ink">Product not found</h2>
          <p className="text-sm text-muted-foreground">It may have been deleted, or the link is incorrect.</p>
          <Button variant="outline" onClick={() => navigate('/products')}>
            Back to products
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      <AssistantReviewContext.Provider value={review.changed}>
      <div className="mx-auto w-full max-w-[1088px] pb-20">
        {review.proposal && <ReviewBanner mode="save" onDiscard={review.discard} className="mb-3" />}
        <PageHeader
          size="large"
          title={product.name}
          badge={product.status ? <StatusBadge type="product" status={product.status} /> : undefined}
          back={<BackButton onClick={() => navigate('/products')} label="Back to products" />}
          actions={
            <>
              <Button variant="destructive" type="button" onClick={() => setDeleteDialogOpen(true)}>
                <Trash2 />
                Delete
              </Button>
              <Button variant="outline" type="button" onClick={() => navigate('/products')}>
                Cancel
              </Button>
              <Button type="button" disabled={isSubmitting} onClick={form.handleSubmit(onSubmit)}>
                {isSubmitting && <Spinner />}
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <UnderlinedTabsList
            tabs={[
              { label: 'General', value: 'general' },
              { label: 'Variants', value: 'variants' },
              { label: 'SEO', value: 'seo' },
              { label: 'Inventory', value: 'inventory' },
            ]}
          />

          <TabsContent value="general" className="mt-4">
            <GeneralSection
              form={form}
              attributes={attributes}
              attributesLoading={attributesLoading}
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={brands}
              brandsLoading={brandsLoading}
            />
          </TabsContent>

          <TabsContent value="variants" className="mt-4">
            <VariantsSection
              attributes={attributes}
              attributesLoading={attributesLoading}
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={brands}
              brandsLoading={brandsLoading}
            />
          </TabsContent>

          <TabsContent value="inventory" className="mt-4">
            <ProductInventorySection
              variations={product.variations ?? []}
              onSuccess={() => id && fetchProduct(id)}
              review={stockReview.proposal ? {
                variationId: stockReview.proposal.payload.variation_id,
                batchId: stockReview.proposal.payload.stock_id,
                quantity: stockReview.proposal.payload.quantity,
                stamp: `${stockReview.proposal.proposal_uid}:${stockReview.stamp}`,
                notice: <ReviewBanner mode="save" onDiscard={stockReview.discard} />,
                onSaved: stockReview.onSaved,
              } : undefined}
            />
          </TabsContent>

          <TabsContent value="seo" className="mt-4">
            <SeoSection form={form} />
          </TabsContent>
        </Tabs>

        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete product?"
          description={`This will permanently delete the product "${product.name}". This action cannot be undone.`}
          confirmText="Delete"
          onConfirm={async () => {
            await handleDelete()
            setDeleteDialogOpen(false)
          }}
          variant="destructive"
        />
      </div>
      </AssistantReviewContext.Provider>
    </FormProvider>
  )
}
